import React, { useState } from 'react';
import { XIcon, SearchIcon, StarIcon, SendIcon, ClockIcon } from 'lucide-react';
import { useTemplates } from '../../hooks/useTemplates';
import { useDebounce } from '../../hooks/useDebounce';

interface TemplatePickerModalProps {
  onSelect: (templateId: string) => void;
  onClose: () => void;
}

export function TemplatePickerModal({ onSelect, onClose }: TemplatePickerModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data, isLoading } = useTemplates({
    search: debouncedSearch,
    is_favorite: showFavoritesOnly || undefined,
    limit: 20,
    sort_by: 'updated_at',
    sort_order: 'desc',
  });

  const templates = data?.templates || [];

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Select Template</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Search & Filters */}
        <div className="px-5 py-3 border-b border-gray-200 space-y-3">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent text-sm"
            />
          </div>

          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              showFavoritesOnly
                ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <StarIcon
              className={`w-4 h-4 ${showFavoritesOnly ? 'fill-yellow-500' : ''}`}
            />
            {showFavoritesOnly ? 'Showing Favorites' : 'Show Favorites Only'}
          </button>
        </div>

        {/* Template List */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="bg-gray-50 rounded-xl p-4 animate-pulse"
                >
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-2">
                {searchQuery ? 'No templates found' : 'No templates available'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-[#5B8E87] text-sm font-medium"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => onSelect(template.id)}
                  className="w-full bg-white border border-gray-200 rounded-xl p-4 hover:border-[#5B8E87] hover:bg-green-50 transition-all text-left"
                >
                  {/* Template Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {template.name}
                        </h3>
                        {template.is_favorite && (
                          <StarIcon className="w-4 h-4 fill-yellow-500 text-yellow-500 flex-shrink-0" />
                        )}
                      </div>
                      {template.category && (
                        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                          {template.category.name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Template Preview */}
                  {template.content.blocks.length > 0 && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {template.content.blocks
                        .filter((b) => b.type === 'text')
                        .map((b) => (b as any).content)
                        .join(' ')
                        .substring(0, 100)}
                      ...
                    </p>
                  )}

                  {/* Template Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <SendIcon className="w-3 h-3" />
                      <span>Used {template.usage_count || 0} times</span>
                    </div>
                    {template.last_used_at && (
                      <div className="flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        <span>{formatRelativeTime(template.last_used_at)}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
